import { Shield, CheckCircle2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * ReproducibleAIBadge - Shows that the AI analysis uses deterministic settings
 * for reproducible scientific results (temperature=0, seed=42)
 */
const ReproducibleAIBadge = ({ variant = "default", className = "" }) => {
  const isCompact = variant === "compact";
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            data-testid="reproducible-ai-badge"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-help transition-all duration-200 hover:scale-105 ${
              isCompact 
                ? "bg-emerald-500/10 border border-emerald-500/30" 
                : "bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/40 shadow-lg shadow-emerald-500/10"
            } ${className}`}
          >
            <Shield className={`${isCompact ? "w-3 h-3" : "w-4 h-4"} text-emerald-400`} />
            <span className={`font-medium ${isCompact ? "text-xs" : "text-sm"} text-emerald-400`}>
              Reproducible AI
            </span>
            <CheckCircle2 className={`${isCompact ? "w-3 h-3" : "w-3.5 h-3.5"} text-emerald-500`} />
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="max-w-xs bg-slate-900 border-slate-700 text-slate-200 p-3"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-semibold text-emerald-400">
              <Shield className="w-4 h-4" />
              Deterministic AI Analysis
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
              This analysis uses reproducible AI settings (temperature=0) ensuring 
              identical results for the same dataset every time. Perfect for scientific 
              reproducibility and quality control.
            </p>
            <div className="flex items-center gap-4 pt-1 text-xs">
              <span className="flex items-center gap-1 text-slate-500">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Consistent
              </span>
              <span className="flex items-center gap-1 text-slate-500">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Verifiable
              </span>
              <span className="flex items-center gap-1 text-slate-500">
                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                Scientific
              </span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ReproducibleAIBadge;
